// PasskeyEnrollPrompt — post-signup "set up a passkey?" offer (Task 11 of
// the 2026-08-30 marketplace-customer-passkeys plan).
//
// Shown by SignInPanel.tsx immediately after the customer's FIRST successful
// OTP verification this browser session, gated on the mount being
// passkey-eligible (see SignInPanel's finishOtpSignIn for the trigger + the
// session-only "already offered" memory). Self-contained: it does its OWN
// platform check rather than trusting a caller to have done one, so it is
// safe to mount standalone (this file's own tests do exactly that).
//
// Both actions ("Set up a passkey" and "Not now") resolve via the SAME
// onDone() callback — the caller decides what "done" means (SignInPanel:
// complete the deferred sign-in-overlay-close transition), this component
// only decides WHEN it is done offering.
//
// Operator ruling R2 (email OTP never gated/degraded): a cancelled or failed
// ceremony is NEVER blocking. Skipping is always one click away via "Not
// now", and a genuine registration failure shows an inline message but still
// leaves "Not now" reachable — there is no path through this component that
// can strand the customer mid-sign-in. Operator ruling R1 (never imply we
// can remove a credential from the device) does not apply to enrollment,
// but the "you can always add one later from Security settings" copy is
// worded to match SecurityPanel's own framing (additive, never a
// replacement for email OTP).
//
// Cancellation detection uses the STABLE `err.name === "NotAllowedError"`
// contract — see embed/marketplace/api/passkeys.ts's header and
// SignInPanel.tsx/SecurityPanel.tsx, which do the same. Never
// `instanceof <class>`: the cancellation error class name is deliberately
// not imported anywhere in this file (an editor auto-import could silently
// bind the wrong one — see api/passkeys.ts's header for the full story).
//
// Rule 73: every <button> sets type="button" at birth — this renders next
// to SignInPanel's <form>, and a bare <button> would default to "submit".
// Rule 8: registerPasskey is awaited directly, no nested .then()/.mutate().

import { useState, useEffect, useCallback } from "react";
import { useEmbedTokens, EmbedButton } from "../../embed-shared/primitives";
import { isPlatformAuthenticatorAvailable } from "../../embed-shared/passkey";
import { registerPasskey } from "./api/passkeys";
import { ceremonyMessageOf } from "./passkey-ceremony-errors";

export interface PasskeyEnrollPromptProps {
  /** Called once the offer is resolved — enrolled, skipped, cancelled, or
   *  found unavailable on this platform. The caller does not need to know
   *  which of those happened. */
  onDone: () => void;
}

export function PasskeyEnrollPrompt({ onDone }: PasskeyEnrollPromptProps) {
  const t = useEmbedTokens();
  // null = still checking; true/false = the platform's verdict.
  const [available, setAvailable] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    isPlatformAuthenticatorAvailable().then((ok) => {
      if (cancelled) return;
      setAvailable(ok);
      if (!ok) {
        // Task 11 fix round 3 (coordinator-widened scope, review
        // IMPORTANT-1a): this is this component's OWN, INDEPENDENT platform
        // probe -- SignInPanel already ran one before ever mounting this
        // component, but the two can disagree (see finishOtpSignIn's doc
        // comment for why a second, authoritative check happens here too).
        // A `false` verdict used to just render null below and stop -- with
        // NO caller ever notified. Since SignInOverlay suppresses its OWN
        // "Not now" while the offer is active, that left a customer whose
        // second probe disagreed staring at a permanently empty card with no
        // visible escape but a reload, which destroys their checkout state
        // -- the identical harm as the stuck-pendingEnrollOffer trap (fix
        // round 2), reached through a different door. Ruling: "not
        // available" must RELEASE the gate, not strand the customer behind
        // it -- reuse the SAME onDone() completion path "Not now" and a
        // finished ceremony already use, never a second release mechanism.
        // Rendering null below is still correct for the VISUAL result; what
        // was missing was firing this alongside it.
        onDone();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [onDone]);

  const handleSetUp = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      // No nickname prompt here — an empty friendly_name mirrors
      // SecurityPanel's own optional-nickname Add flow.
      await registerPasskey("");
      onDone();
    } catch (err) {
      if (err instanceof Error && err.name === "NotAllowedError") {
        // The customer dismissed the OS/browser prompt — a normal outcome,
        // never an error (design ruling). Finish exactly as "Not now" would.
        onDone();
        return;
      }
      // fix-round-2 I-3: surfaces the real WebAuthn reason (e.g. a permanent
      // SecurityError) via ceremonyMessageOf instead of always the generic
      // fallback — see that module's header and SecurityPanel.tsx, the
      // original site of this treatment. "Not now" (below) stays reachable
      // regardless of which message renders — R2, this step never blocks
      // sign-in, and no copy here may imply the account is otherwise locked.
      setError(
        ceremonyMessageOf(err, "Something went wrong. You can add a passkey anytime from Security settings."),
      );
      setBusy(false);
    }
  }, [onDone]);

  const handleSkip = useCallback(() => {
    onDone();
  }, [onDone]);

  // Hidden until the platform check resolves true — a "still checking" or
  // "no" verdict renders nothing rather than a form the customer cannot use.
  // A resolved "no" ALSO calls onDone() above (fix round 3) so the caller
  // ungates instead of leaving the customer behind a permanently empty
  // card — this line only governs what paints, not whether the offer ends.
  if (available !== true) return null;

  return (
    <div>
      <h2
        style={{
          fontSize: t.fontSize.xl,
          fontWeight: 700,
          color: t.colors.text,
          margin: `0 0 ${t.spacing.sm}px 0`,
          textAlign: "center",
          lineHeight: 1.3,
        }}
      >
        Set up a passkey?
      </h2>
      <p
        style={{
          color: t.colors.textSecondary,
          fontSize: t.fontSize.sm,
          textAlign: "center",
          margin: `0 0 ${t.spacing.md}px 0`,
        }}
      >
        Skip the code next time — sign in with Touch ID, Face ID, Windows
        Hello, or a security key instead. Signing in by email still works
        exactly as it does today; this is an extra option, not a
        replacement. You can always add one later from Security settings.
      </p>
      {error && (
        <p
          role="alert"
          aria-live="assertive"
          style={{ color: t.colors.error, fontSize: t.fontSize.xs, margin: `0 0 ${t.spacing.sm}px 0` }}
        >
          {error}
        </p>
      )}
      <EmbedButton type="button" variant="primary" fullWidth loading={busy} disabled={busy} onClick={handleSetUp}>
        Set up a passkey
      </EmbedButton>
      <div style={{ textAlign: "center", marginTop: t.spacing.sm }}>
        {/* Deliberately NOT disabled={busy} (Task 11 fix round 2, review
            IMPORTANT-1b): registerPasskey() passes no AbortSignal to either
            fetch leg, so a stalled network mid-ceremony left BOTH buttons
            disabled with the overlay's own dismiss also suppressed
            (enrollPromptActive) -- an inert page recoverable only by a
            reload, which destroys checkout state. Ruling: data loss during
            checkout outranks a double-submit risk on a dismiss button. A
            click here while busy just calls onDone() -- registerPasskey()'s
            in-flight promise is not cancelled by it (no AbortController
            exists to cancel), so it keeps running in the background and
            resolves/rejects into a possibly-unmounted component; that's a
            harmless stray console warning at worst, never a functional
            failure, and strictly better than stranding the customer. */}
        <EmbedButton type="button" variant="ghost" size="sm" onClick={handleSkip}>
          Not now
        </EmbedButton>
      </div>
    </div>
  );
}
